import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './CadDocumentCreator.css';

import {
  selectEditorState,
  openEditor,
  closeEditor,
  setEditorDxf,
  bindVariable,
  unbindVariable,
  setTemplateName,
} from '../../redux/baslikTemplateSlice';

import { VARIABLE_CATALOG } from '../../config/baslikConfig/variableCatalog';
import { useTemplatePreviewScene } from '../../hooks/useTemplatePreviewScene';
import { useTemplateDxfLoader } from '../../hooks/useTemplateDxfLoader';
import { useTemplateTextSelection } from '../../modules/selection';
import { dxfDrawCore } from '../../modules/dxf';
import { buildMarkaEntities } from '../../modules/documents';
import { getFirmaInfo, emptyMarkaData } from '../../services/firmaInfo';

const MAX_DXF_SIZE = 100 * 1024 * 1024;  // 100 MB — OLE-gömülü büyük DXF'ler

const newNoteId  = () => 'n_'   + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

const SUB_TYPES = [
  { value: 'baslik', label: 'Başlık' },
  { value: 'notlar', label: 'Notlar' },
];

// Marka formu alan tanımları (etiket sırası)
const MARKA_BRAND_FIELDS = [
  ['isim', 'Marka İsmi (büyük)'],
  ['satir1', 'Alt Satır 1'],
  ['satir2', 'Alt Satır 2'],
];
const MARKA_FIRMA_FIELDS = [
  ['firmaAdi', 'Firma Adı'],
  ['firmaTamAdi', 'Firma Tam Adı'],
  ['adres', 'Adres'],
  ['tel', 'Tel'],
  ['vergiDairesi', 'Vergi Dairesi'],
  ['vergiNo', 'Vergi No'],
];
const MARKA_SORUMLU_FIELDS = [
  ['adiSoyadi', 'Adı Soyadı'],
  ['unvan', 'Ünvan'],
  ['odaNo', 'Oda No'],
  ['tcNo', 'Tc No'],
  ['gsm', 'Gsm'],
];
const SUB_TYPE_LABEL = Object.fromEntries(SUB_TYPES.map(s => [s.value, s.label]));

// Notlar entity üretici — MTEXT içine \P satır sonlarıyla numaralı maddeler yazar.
function buildNotlarEntities(title, notes) {
  const items = [];
  if (title && title.trim()) items.push(title.trim());
  notes.forEach((n, i) => {
    const t = (n.text || '').trim();
    if (t) items.push(`${i + 1}- ${t}`);
  });
  if (items.length === 0) return [];
  // MTEXT formatı: \fFont|...;\C<color>;text\Pnext\P...
  const mtext = '{\\fTimes New Roman|b0|i0|c162|p18;\\C7;' + items.join('\\P') + '}';
  return [{
    type: 'MTEXT',
    text: mtext,
    position: { x: 0, y: 1500, z: 0 },
    height: 16,
    width: 1000,
    attachmentPoint: 1,    // top-left
    drawingDirection: 5,
    layer: '0',
  }];
}

/**
 * CAD Doküman Oluşturucu
 * - Sol: özellikler paneli (DXF text → supply key bağlama)
 *
 * NOT: Görsel/marka ekleme özelliği kaldırıldı. Kullanıcı markasını AutoCAD gibi
 * bir ortamda OLE olarak gömer, o DXF'i burada açar ve sadece alan eşlemesi yapar.
 * OLE app'te dxfDrawCore ile render edilir, export'ta olduğu gibi (passthrough) kullanılır.
 * - Sağ: CAD viewer (Three.js)
 * - Save: backend /api/documents (type=cad_template) + redux baslikTemplate slice
 */
const CadDocumentCreator = ({ onClose, onCreated, apiUrl, token }) => {
  const dispatch = useDispatch();
  const editorState = useSelector(selectEditorState);
  const { dxfRawText, textEntities, selectedEntityKey, pendingBindings, templateName } = editorState;

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Doküman türü: 'baslik' | 'notlar'
  const [subType, setSubType] = useState('baslik');

  // Notlar state
  const [notlarTitle, setNotlarTitle] = useState('NOTLAR:');
  const [notes, setNotes] = useState([{ id: newNoteId(), text: '' }]);

  // Marka (firma alanı) state — hesaptan (Ayarlar → Firma Bilgileri) çekilir, bu
  // şablon için düzenlenebilir. Başlığın Marka hücresine native geometriyle yazılır.
  const [markaData, setMarkaData] = useState(() => emptyMarkaData());
  const [markaOleOn, setMarkaOleOn] = useState(false);   // "Firma Alanı" — Marka OLE göster/gizle
  const [markaNativeOn, setMarkaNativeOn] = useState(false); // Native firma bilgisi göster/gizle
  // Marka isim şekli döngüsü — 0: yok, 1: iç şekil, 2: iç+dış şekil
  const [markaCircle, setMarkaCircle] = useState(2); // daire
  const [markaSquare, setMarkaSquare] = useState(0); // kare

  const viewerRef = useRef(null);
  const fileInputRef = useRef(null);

  const { scene: sceneRef, camera: cameraRef, renderer: rendererRef, clearScene, fitToContent } =
    useTemplatePreviewScene(viewerRef);

  const { loadTemplateDxf } = useTemplateDxfLoader();
  const { handleClick: handleTextClick } = useTemplateTextSelection(
    sceneRef, cameraRef, rendererRef, textEntities
  );

  const parsedDxfRef = useRef(null);
  const lastRenderedRef = useRef(null);

  const [openCats, setOpenCats] = useState(() => {
    const map = {};
    VARIABLE_CATALOG.forEach(c => { map[c.category] = true; });
    return map;
  });

  // Mount edildiğinde slice'ı sıfırla (component her açılışta yeniden mount edilir)
  useEffect(() => {
    dispatch(openEditor());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mount'ta hesaptan firma/sorumlu bilgisini çek (Firma Bilgileri formu pre-fill)
  useEffect(() => {
    setMarkaData(getFirmaInfo());
  }, []);

  // Tek render dispatcher — subType, dxf, notlar değişince sahneyi yeniden çiz
  useEffect(() => {
    if (!sceneRef.current) return;
    clearScene();

    if (subType === 'baslik') {
      if (!dxfRawText || !parsedDxfRef.current) return;
      const { dxfData, layers } = parsedDxfRef.current;
      try {
        let entities = dxfData.entities || [];
        // "Firma Alanı" (Marka OLE) toggle kapalıysa OLE2FRAME'leri çizme
        if (!markaOleOn) entities = entities.filter(e => e.type !== 'OLE2FRAME');
        const blocks = dxfData.blocks || {};
        dxfDrawCore(sceneRef.current, entities, blocks, 'scene', layers);
        let textIdx = 0, mtextIdx = 0;
        sceneRef.current.traverse((obj) => {
          if (obj.userData) {
            if (obj.userData.entityType === 'TEXT' || obj.userData.type === 'text') {
              obj.userData.entityKey = `text_${textIdx++}`;
            } else if (obj.userData.entityType === 'MTEXT' || obj.userData.type === 'mtext') {
              obj.userData.entityKey = `mtext_${mtextIdx++}`;
            }
          }
        });
        // Native firma bilgisi toggle'ı açıksa Marka hücresine overlay çiz
        // (key ataması SONRASI — marka MTEXT'leri bağlanabilir listeye girmesin).
        // Marka hücresini DXF'teki OLE2FRAME sınırlarından tespit et → native metin
        // tam OLE'nin yerine gelir (sabit koordinat değil). OLE yoksa varsayılan.
        if (markaNativeOn) {
          const oleEnt = (dxfData.entities || []).find(en => en.type === 'OLE2FRAME');
          const shapeOpts = { circleMode: markaCircle, squareMode: markaSquare };
          let mo = { drawFrame: false, ...shapeOpts };
          if (oleEnt?.upperLeft && oleEnt?.lowerRight) {
            const ul = oleEnt.upperLeft, lr = oleEnt.lowerRight;
            const x0 = Math.min(ul.x, lr.x), x1 = Math.max(ul.x, lr.x);
            const y0 = Math.min(ul.y, lr.y), y1 = Math.max(ul.y, lr.y);
            if (x1 > x0 && y1 > y0) {
              mo = { drawFrame: false, originX: x0, originY: y0, width: x1 - x0, height: y1 - y0, ...shapeOpts };
            }
          }
          const markaEnts = buildMarkaEntities(markaData, mo);
          if (markaEnts.length) dxfDrawCore(sceneRef.current, markaEnts, {}, 'scene', []);
        }
      } catch (err) {
        console.error('[CadDocumentCreator] DXF render hatası:', err);
      }
      lastRenderedRef.current = dxfRawText;
      setTimeout(() => fitToContent(), 200);
    } else if (subType === 'notlar') {
      const entities = buildNotlarEntities(notlarTitle, notes);
      if (entities.length === 0) return;
      try {
        dxfDrawCore(sceneRef.current, entities, {}, 'scene', []);
      } catch (err) {
        console.error('[CadDocumentCreator] Notlar render hatası:', err);
      }
      setTimeout(() => fitToContent(), 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subType, dxfRawText, notes, notlarTitle, markaData, markaOleOn, markaNativeOn, markaCircle, markaSquare]);

  // ─── DXF Yükleme ─────────────────────────────────────
  const handleDxfChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_DXF_SIZE) {
      setLoadError(`Dosya çok büyük (${(file.size / 1024 / 1024).toFixed(1)} MB). Max ${MAX_DXF_SIZE / 1024 / 1024} MB.`);
      e.target.value = '';
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    lastRenderedRef.current = null;

    setTimeout(() => {
      loadTemplateDxf(file, (result) => {
        parsedDxfRef.current = result;
        dispatch(setEditorDxf({
          dxfRawText: result.dxfRawText,
          textEntities: result.textEntities,
        }));
        setIsLoading(false);
      }, (err) => {
        console.error('[CadDocumentCreator] DXF yüklenemedi:', err);
        setLoadError('DXF okunamadı: ' + (err.message || 'Bilinmeyen hata'));
        setIsLoading(false);
      });
    }, 50);
    e.target.value = '';
  }, [dispatch, loadTemplateDxf]);

  // ─── Viewer Click (text seçimi → alan bağlama) ───────
  const handleViewerClick = useCallback((e) => {
    if (!dxfRawText || isLoading) return;
    handleTextClick(e);
  }, [dxfRawText, isLoading, handleTextClick]);

  // ─── Bağlama Handler'ları ────────────────────────────
  const handleBindVariable = useCallback((variablePath) => {
    if (!selectedEntityKey) return;
    const entity = textEntities.find(te => te.key === selectedEntityKey);
    if (!entity) return;
    dispatch(bindVariable({
      entityKey: selectedEntityKey,
      variablePath,
      originalText: entity.text,
      entityType: entity.type,
    }));
  }, [dispatch, selectedEntityKey, textEntities]);

  const handleUnbind = useCallback((entityKey) => {
    dispatch(unbindVariable(entityKey));
  }, [dispatch]);

  const toggleCat = useCallback((cat) => {
    setOpenCats(prev => ({ ...prev, [cat]: !prev[cat] }));
  }, []);

  // ─── Kaydet ─────────────────────────────────────────
  const hasNonEmptyNote = notes.some(n => (n.text || '').trim());
  const canSave = !saving && !!templateName.trim() && (
    subType === 'baslik' ? !!dxfRawText : hasNonEmptyNote
  );

  const handleSave = useCallback(async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);

    try {
      const safeName = templateName.trim().replace(/[^a-zA-Z0-9_\-]/g, '_');
      const form = new FormData();
      form.append('title', templateName.trim());
      form.append('type', 'cad_template');
      form.append('subType', subType);
      form.append('discipline', 'electrical');

      if (subType === 'baslik') {
        const dxfBlob = new Blob([dxfRawText], { type: 'application/dxf' });
        const dxfFile = new File([dxfBlob], `${safeName || 'baslik'}.dxf`, { type: 'application/dxf' });
        form.append('file', dxfFile);
        form.append('description', 'CAD başlık dokümanı');
        form.append('tags', JSON.stringify(['başlık', 'cad']));
        form.append('payload', JSON.stringify({
          subType: 'baslik',
          bindings: pendingBindings,
          // Marka (firma) alanı ayarları — başlığın Marka hücresi için
          marka: { ole: markaOleOn, native: markaNativeOn, circle: markaCircle, square: markaSquare, data: markaData },
        }));
      } else if (subType === 'notlar') {
        form.append('description', 'CAD notlar dokümanı');
        form.append('tags', JSON.stringify(['notlar', 'cad']));
        form.append('payload', JSON.stringify({
          subType: 'notlar',
          title: notlarTitle,
          notes: notes
            .map(n => ({ id: n.id, text: (n.text || '').trim() }))
            .filter(n => n.text),
        }));
        // file alanı opsiyonel — notlar için DXF yok
      }

      const res = await fetch(`${apiUrl}/api/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (onCreated) onCreated(data.document || data);
      handleClose();
    } catch (err) {
      console.error('[CadDocumentCreator] kaydetme hatası:', err);
      setSaveError(err.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSave, subType, dxfRawText, templateName, pendingBindings, notes, notlarTitle, markaData, markaOleOn, markaNativeOn, markaCircle, markaSquare, apiUrl, token]);

  const handleClose = useCallback(() => {
    parsedDxfRef.current = null;
    lastRenderedRef.current = null;
    dispatch(closeEditor());
    if (onClose) onClose();
  }, [dispatch, onClose]);

  const selectedEntity = selectedEntityKey ? textEntities.find(te => te.key === selectedEntityKey) : null;
  const selectedBinding = selectedEntityKey ? pendingBindings[selectedEntityKey] : null;
  const boundPaths = new Set(Object.values(pendingBindings).map(b => b.variablePath));

  return (
    <div className="cdc-overlay" onClick={handleClose}>
      <div className="cdc-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cdc-header">
          <div>
            <h2>CAD Doküman Oluşturucu<span className="cdc-header-sub">— {SUB_TYPE_LABEL[subType] || ''}</span></h2>
          </div>
          <button className="cdc-close-btn" onClick={handleClose} title="Kapat">&times;</button>
        </div>

        {/* Body */}
        <div className="cdc-body">
          {/* Sol: özellikler */}
          <div className="cdc-properties">
            {/* Doküman türü + adı */}
            <div className="cdc-section">
              <div className="cdc-section-title">Doküman</div>
              <label className="cdc-label">Türü</label>
              <select
                className="cdc-select"
                value={subType}
                onChange={e => setSubType(e.target.value)}
              >
                {SUB_TYPES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <label className="cdc-label" style={{ marginTop: 8 }}>Adı</label>
              <input
                className="cdc-input"
                type="text"
                value={templateName}
                onChange={e => dispatch(setTemplateName(e.target.value))}
                placeholder={subType === 'notlar' ? 'Örn: Elektrik Notları' : 'Örn: Belediye Başlık'}
              />
            </div>

            {/* ═══════════════════════════════════════════════════ */}
            {/* NOTLAR EDITOR (subType === 'notlar')               */}
            {/* ═══════════════════════════════════════════════════ */}
            {subType === 'notlar' && (
              <>
                <div className="cdc-section">
                  <div className="cdc-section-title">Başlık Metni</div>
                  <input
                    className="cdc-input"
                    type="text"
                    value={notlarTitle}
                    onChange={e => setNotlarTitle(e.target.value)}
                    placeholder="NOTLAR:"
                  />
                </div>

                <div className="cdc-section">
                  <div className="cdc-section-title">Maddeler ({notes.length})</div>
                  {notes.map((n, idx) => (
                    <div key={n.id} className="cdc-note-row">
                      <span className="cdc-note-num">{idx + 1}.</span>
                      <textarea
                        className="cdc-note-input"
                        value={n.text}
                        onChange={e => {
                          const v = e.target.value;
                          setNotes(prev => prev.map(x => x.id === n.id ? { ...x, text: v } : x));
                        }}
                        placeholder="Madde metni..."
                        rows={2}
                      />
                      <div className="cdc-note-actions">
                        <button
                          className="cdc-note-btn"
                          title="Yukarı taşı"
                          onClick={() => {
                            if (idx === 0) return;
                            setNotes(prev => {
                              const arr = prev.slice();
                              [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                              return arr;
                            });
                          }}
                          disabled={idx === 0}
                        >&#9650;</button>
                        <button
                          className="cdc-note-btn"
                          title="Aşağı taşı"
                          onClick={() => {
                            if (idx === notes.length - 1) return;
                            setNotes(prev => {
                              const arr = prev.slice();
                              [arr[idx + 1], arr[idx]] = [arr[idx], arr[idx + 1]];
                              return arr;
                            });
                          }}
                          disabled={idx === notes.length - 1}
                        >&#9660;</button>
                        <button
                          className="cdc-note-btn cdc-note-btn-danger"
                          title="Sil"
                          onClick={() => {
                            setNotes(prev => prev.length === 1
                              ? [{ id: newNoteId(), text: '' }]
                              : prev.filter(x => x.id !== n.id)
                            );
                          }}
                        >&times;</button>
                      </div>
                    </div>
                  ))}
                  <button
                    className="cdc-btn"
                    style={{ marginTop: 6, width: '100%' }}
                    onClick={() => setNotes(prev => [...prev, { id: newNoteId(), text: '' }])}
                  >
                    + Madde Ekle
                  </button>
                </div>
              </>
            )}

            {/* ═══════════════════════════════════════════════════ */}
            {/* BAŞLIK MODU (subType === 'baslik')                 */}
            {/* ═══════════════════════════════════════════════════ */}

            {/* Firma (Marka) Alanı — başlığın Marka hücresi için */}
            {subType === 'baslik' && (
              <div className="cdc-section">
                <div className="cdc-section-title">Marka İsmi (sol alan)</div>
                {MARKA_BRAND_FIELDS.map(([key, label]) => (
                  <div key={key}>
                    <label className="cdc-label">{label}</label>
                    <input
                      className="cdc-input"
                      type="text"
                      value={markaData.brand[key] || ''}
                      onChange={e => {
                        const v = e.target.value;
                        setMarkaData(d => ({ ...d, brand: { ...d.brand, [key]: v } }));
                      }}
                    />
                  </div>
                ))}

                <div className="cdc-section-title" style={{ marginTop: 10 }}>Firma Bilgileri</div>
                {MARKA_FIRMA_FIELDS.map(([key, label]) => (
                  <div key={key}>
                    <label className="cdc-label">{label}</label>
                    <input
                      className="cdc-input"
                      type="text"
                      value={markaData.firma[key] || ''}
                      onChange={e => {
                        const v = e.target.value;
                        setMarkaData(d => ({ ...d, firma: { ...d.firma, [key]: v } }));
                      }}
                    />
                  </div>
                ))}

                <div className="cdc-section-title" style={{ marginTop: 10 }}>Proje Sorumlusu</div>
                {MARKA_SORUMLU_FIELDS.map(([key, label]) => (
                  <div key={key}>
                    <label className="cdc-label">{label}</label>
                    <input
                      className="cdc-input"
                      type="text"
                      value={markaData.sorumlu[key] || ''}
                      onChange={e => {
                        const v = e.target.value;
                        setMarkaData(d => ({ ...d, sorumlu: { ...d.sorumlu, [key]: v } }));
                      }}
                    />
                  </div>
                ))}

                <button
                  className="cdc-btn"
                  style={{ width: '100%', marginTop: 8 }}
                  onClick={() => setMarkaData(getFirmaInfo())}
                >
                  Hesaptan yeniden çek
                </button>
                <div className="cdc-muted" style={{ marginTop: 4 }}>
                  Değerler hesabından (Ayarlar → Firma Bilgileri) geldi. Bu şablon için
                  düzenleyebilirsin — hesabı değiştirmez.
                </div>

                {/* Marka alanı toggle'ları */}
                <button
                  className={`cdc-btn ${markaOleOn ? 'cdc-btn-active' : ''}`}
                  style={{ width: '100%', marginTop: 10 }}
                  onClick={() => setMarkaOleOn(v => !v)}
                  title="Başlıktaki Marka OLE (gömülü görsel) alanını göster/gizle"
                >
                  Firma Alanı (Marka OLE): {markaOleOn ? 'Açık ✓' : 'Kapalı'}
                </button>
                <button
                  className={`cdc-btn ${markaNativeOn ? 'cdc-btn-active' : ''}`}
                  style={{ width: '100%', marginTop: 6 }}
                  onClick={() => setMarkaNativeOn(v => !v)}
                  title="Firma bilgilerini native geometriyle Marka alanına yaz (logo hariç)"
                >
                  Native Firma Bilgileri: {markaNativeOn ? 'Açık ✓' : 'Kapalı'}
                </button>

                {/* Marka ismini kapsayan şekiller — tıkla: 1 → 2 (iç içe) → gizle */}
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button
                    className={`cdc-btn ${markaCircle > 0 ? 'cdc-btn-active' : ''}`}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    onClick={() => setMarkaCircle(v => (v + 1) % 3)}
                    title="Daire — tıkla: 1 daire → 2 iç içe daire → gizle"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /></svg>
                    Daire: {markaCircle === 0 ? 'Yok' : markaCircle}
                  </button>
                  <button
                    className={`cdc-btn ${markaSquare > 0 ? 'cdc-btn-active' : ''}`}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    onClick={() => setMarkaSquare(v => (v + 1) % 3)}
                    title="Kare — tıkla: 1 kare → 2 iç içe kare → gizle"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3.5" y="3.5" width="17" height="17" rx="1" /></svg>
                    Kare: {markaSquare === 0 ? 'Yok' : markaSquare}
                  </button>
                </div>
              </div>
            )}

            {/* Seçili Text */}
            {subType === 'baslik' && (
            <div className="cdc-section">
              <div className="cdc-section-title">Seçili Text</div>
              {!selectedEntity ? (
                <div className="cdc-muted">CAD viewer'da bir text'e tıklayın</div>
              ) : (
                <>
                  <div className="cdc-selected-key">{selectedEntity.key} ({selectedEntity.type})</div>
                  <div className="cdc-selected-text">"{selectedEntity.text}"</div>
                  {selectedBinding ? (
                    <>
                      <div className="cdc-selected-bound">Bağlı: {selectedBinding.variablePath}</div>
                      <button className="cdc-btn cdc-btn-danger" onClick={() => handleUnbind(selectedEntityKey)}>
                        Bağlamayı Kaldır
                      </button>
                    </>
                  ) : (
                    <div className="cdc-muted">Aşağıdan bir supply değişkeni seçin</div>
                  )}
                </>
              )}
            </div>
            )}

            {/* Supply Katalog */}
            {subType === 'baslik' && (
            <div className="cdc-section">
              <div className="cdc-section-title">Supply (Değişken Kataloğu)</div>
              <div className="cdc-catalog">
                {VARIABLE_CATALOG.map(cat => {
                  const isOpen = openCats[cat.category] !== false;
                  return (
                    <div key={cat.category}>
                      <div className="cdc-catalog-cat" onClick={() => toggleCat(cat.category)}>
                        <span className={`cdc-catalog-chevron ${isOpen ? 'open' : ''}`}>&#9654;</span>
                        {cat.category}
                      </div>
                      <div className={`cdc-catalog-items ${isOpen ? '' : 'collapsed'}`}>
                        {cat.variables.map(v => (
                          <div
                            key={v.path}
                            className={`cdc-catalog-item ${(!selectedEntityKey || boundPaths.has(v.path)) ? 'disabled' : ''}`}
                            onClick={() => handleBindVariable(v.path)}
                            title={v.path}
                          >
                            {v.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {/* Bağlamalar */}
            {subType === 'baslik' && (
            <div className="cdc-section">
              <div className="cdc-section-title">Bağlamalar ({Object.keys(pendingBindings).length})</div>
              {Object.keys(pendingBindings).length === 0 ? (
                <div className="cdc-muted">Henüz bağlama yok</div>
              ) : (
                Object.entries(pendingBindings).map(([key, binding]) => (
                  <div key={key} className="cdc-binding-row">
                    <span className="cdc-binding-key">{key}</span>
                    <span className="cdc-binding-arrow">&rarr;</span>
                    <span className="cdc-binding-path" title={binding.variablePath}>{binding.variablePath}</span>
                    <button className="cdc-binding-remove" onClick={() => handleUnbind(key)} title="Kaldır">&times;</button>
                  </div>
                ))
              )}
            </div>
            )}

          </div>

          {/* Sağ: viewer */}
          <div className="cdc-viewer-wrap">
            <div
              className="cdc-viewer"
              ref={viewerRef}
              onClick={subType === 'baslik' ? handleViewerClick : undefined}
            >
              {subType === 'baslik' && !dxfRawText && !isLoading && (
                <div className="cdc-viewer-empty">DXF dosyası yükleyin</div>
              )}
              {subType === 'baslik' && isLoading && <div className="cdc-viewer-empty">Yükleniyor...</div>}
              {subType === 'notlar' && !hasNonEmptyNote && (
                <div className="cdc-viewer-empty">Sol panele not yazın — canlı önizleme burada görünür</div>
              )}
            </div>
            <div className="cdc-viewer-bar">
              {subType === 'baslik' && (
                <>
                  <button className="cdc-primary-btn" onClick={() => fileInputRef.current?.click()} disabled={isLoading}>
                    {isLoading ? 'Yükleniyor...' : 'DXF Yükle'}
                  </button>
                  <input
                    ref={fileInputRef}
                    className="cdc-hidden-input"
                    type="file"
                    accept=".dxf"
                    onChange={handleDxfChange}
                  />
                  {loadError && <span className="cdc-error">{loadError}</span>}
                  {dxfRawText && !isLoading && (
                    <span className="cdc-info">{textEntities.length} text bulundu</span>
                  )}
                </>
              )}
              {subType === 'notlar' && (
                <span className="cdc-info">
                  {notes.filter(n => n.text.trim()).length} madde · canlı önizleme
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="cdc-footer">
          {saveError && <span className="cdc-error" style={{ marginRight: 'auto', alignSelf: 'center' }}>{saveError}</span>}
          <button className="cdc-cancel" onClick={handleClose}>İptal</button>
          <button className="cdc-save" onClick={handleSave} disabled={!canSave}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CadDocumentCreator;
